import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, isRef as _isRef, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "map-view" }

import MainMap from "@/components/modules/MainMap/MainMap.vue";
import {useFilesStore} from "@/stores/files";
import {onMounted, ref} from "vue";
import MapToolbar from "@/components/modules/MainMap/MapToolbar.vue";
import {useStore} from "@/stores";
import {storeToRefs} from "pinia";
import type {MapDisplayParamsKey} from "@/composables/constants/params";
import {FederalDistrictDataQuery, FilesDataQuery} from "@/types/files";
import MapLegend from "@/components/modules/MainMap/MapLegend.vue";
import MapAnalytics from "@/components/modules/Analytics/MapAnalytics.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'MapView',
  setup(__props) {

const filesStore = useFilesStore();
const mainStore = useStore();

const {selectedDistrictKey, selectedRegionKey} = storeToRefs(filesStore);
const {forecastStatusDisplay} = storeToRefs(mainStore);

// Флаг загрузки данных для карты.
const isDataLoading = ref(false);

// Флаг загрузки карты.
const isMapLoading = ref(false);

// Активный выбранный параметр
const selectedParams = ref<MapDisplayParamsKey>("cases_accepted");

// Активный месяц и год
const { selectedMonth } = storeToRefs(filesStore);
const { selectedYear } = storeToRefs(filesStore);

onMounted(async (): Promise<void> => {
  await loadData();
});

// Функция загрузки данных файлов
const loadFilesDataOnly = async () => {
  const params: FilesDataQuery = {};

  // Выбранный месяц, год.
  if (selectedMonth.value !== "all") {
    params.month = selectedMonth.value;
  }

  params.year = selectedYear.value;

  // Фильтр по округу (только если не выбран конкретный регион)
  if (selectedDistrictKey.value && !selectedRegionKey.value) {
    params.federal_districts = selectedDistrictKey.value;
  }

  // Фильтр по региону (имеет приоритет над округом)
  if (selectedRegionKey.value) {
    params.regions = selectedRegionKey.value;
  }

  await filesStore.getFilesData(params);
};

// Функция загрузки данных по федеральным округам
const loadFederalDistrictDataOnly = async () => {
  const params: FederalDistrictDataQuery = {
    metricName: selectedParams.value,
    year: selectedYear.value,
  };

  // Выбранный месяц
  if (selectedMonth.value !== "all") {
    params.month = selectedMonth.value;
  }

  await filesStore.getFederalDistrictData(params);
};

// Функция загрузки прогноза
const loadForecast = async () => {
  if (!forecastStatusDisplay.value) {
    return;
  }

  try {
    const region = selectedRegionKey.value ? selectedRegionKey.value : undefined;
    const district = selectedDistrictKey.value ? selectedDistrictKey.value : undefined;

    await mainStore.getForecast(region, district);
  } catch (error) {
    console.error("Ошибка загрузки прогноза:", error);
    mainStore.showWarning("Ошибка", "Не удалось загрузить данные прогноза");
  }
};

// Общая функция загрузки всех данных
const loadData = async () => {
  try {
    isMapLoading.value = true;
    isDataLoading.value = true;

    await Promise.all([
      loadFilesDataOnly(),
      loadFederalDistrictDataOnly(),
      loadForecast()
    ]);
  } catch (error) {
    console.error("Ошибка загрузки данных:", error);
    mainStore.showWarning("Ошибка", "Не удалось загрузить данные за выбранный период");
  } finally {
    setTimeout(() => {
      console.log("set timeout");
      isMapLoading.value = false;
      isDataLoading.value = false;
    }, 1);
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(MapToolbar, {
      "selected-params": selectedParams.value,
      "onUpdate:selectedParams": [
        _cache[0] || (_cache[0] = ($event: any) => ((selectedParams).value = $event)),
        loadData
      ],
      "selected-month": _unref(selectedMonth),
      "onUpdate:selectedMonth": [
        _cache[1] || (_cache[1] = ($event: any) => (_isRef(selectedMonth) ? (selectedMonth).value = $event : null)),
        loadData
      ],
      "selected-year": _unref(selectedYear),
      "onUpdate:selectedYear": [
        _cache[2] || (_cache[2] = ($event: any) => (_isRef(selectedYear) ? (selectedYear).value = $event : null)),
        loadData
      ],
      "selected-district": _unref(selectedDistrictKey),
      "onUpdate:selectedDistrict": [
        _cache[3] || (_cache[3] = ($event: any) => (_isRef(selectedDistrictKey) ? (selectedDistrictKey).value = $event : null)),
        loadData
      ],
      "selected-region": _unref(selectedRegionKey),
      "onUpdate:selectedRegion": [
        _cache[4] || (_cache[4] = ($event: any) => (_isRef(selectedRegionKey) ? (selectedRegionKey).value = $event : null)),
        loadData
      ]
    }, null, 8, ["selected-params", "selected-month", "selected-year", "selected-district", "selected-region"]),
    _createVNode(MainMap, {
      "is-data-loading": isDataLoading.value,
      "onUpdate:isDataLoading": _cache[5] || (_cache[5] = ($event: any) => ((isDataLoading).value = $event)),
      "is-map-loading": isMapLoading.value,
      "onUpdate:isMapLoading": _cache[6] || (_cache[6] = ($event: any) => ((isMapLoading).value = $event)),
      "selected-district": _unref(selectedDistrictKey),
      "onUpdate:selectedDistrict": [
        _cache[7] || (_cache[7] = ($event: any) => (_isRef(selectedDistrictKey) ? (selectedDistrictKey).value = $event : null)),
        loadData
      ],
      "selected-region": _unref(selectedRegionKey),
      "onUpdate:selectedRegion": [
        _cache[8] || (_cache[8] = ($event: any) => (_isRef(selectedRegionKey) ? (selectedRegionKey).value = $event : null)),
        loadData
      ],
      "selected-params": selectedParams.value
    }, null, 8, ["is-data-loading", "is-map-loading", "selected-district", "selected-region", "selected-params"]),
    _createVNode(MapLegend, { "param-name": selectedParams.value }, null, 8, ["param-name"]),
    _createVNode(MapAnalytics, {
      "selected-district": _unref(selectedDistrictKey),
      "selected-region": _unref(selectedRegionKey),
      "param-name": selectedParams.value,
      "selected-year": _unref(selectedYear),
      "selected-month": _unref(selectedMonth)
    }, null, 8, ["selected-district", "selected-region", "param-name", "selected-year", "selected-month"])
  ]))
}
}

})